import React, { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import GenericPage from '../../components/GenericPage';
import {
    fetchDeliverynotes,
    createDeliveryNote,
    updateDeliveryNote,
    deleteDeliveryNote,
    setPagination,
    setSort,
    searchDeliverynotes,
    setDefaultFilter,
    changeCondition,
    addNewCondition,
    updateOptionRule,
    updateOptionValue,
    removeCondition,
    saveFilterCondition,
    setSelected,
    resetFiltersAndPagination
} from '../../store/modules/deliverynote/deliverynoteActions';
import DeliveryNoteForm from '../../components/pageModule/Deliverynotes/DeliveryNoteForm';
import { useHasPermission } from '../../utils/permissionUtils';
const MySwal = withReactContent(Swal);

const Deliverynotes = ({
    excludedActions = [],
    onSelectedRowsChange = () => { },
    onClickedRow = () => { },
    externalCondition = {},
    selectedIds = [] }) => {

    const hasCreatePermission = useHasPermission(['create delivery notes']);
    const hasUpdatePermission = useHasPermission(['update delivery notes']);
    const hasViewPermission = useHasPermission(['view delivery notes']);

    const deliverynoteState = useSelector(state => state.deliverynote);
    const dispatch = useDispatch();
    useEffect(() => {
        return () => {
            dispatch(resetFiltersAndPagination());
        };
    }, []);

    const handleCreate = async (formData) => {
        try {
            const response = await dispatch(createDeliveryNote(formData));
            toast.success('DeliveryNote created successfully', {
                position: "top-right",
                autoClose: 3000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
            });
            return response;
        } catch (error) {
            toast.error(error.message || 'Error creating deliverynote', {
                position: "top-right",
                autoClose: 3000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
            });
            throw error;
        }
    };

    const handleUpdate = async (id, formData) => {
        try {
            const response = await dispatch(updateDeliveryNote(id, formData));
            toast.success('DeliveryNote updated successfully', {
                position: "top-right",
                autoClose: 3000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
            });
            return response;
        } catch (error) {
            toast.error(error.message || 'Error updating deliverynote', {
                position: "top-right",
                autoClose: 3000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
            });
            throw error;
        }
    };

    const handleDelete = async (id) => {
        const result = await MySwal.fire({
            title: 'Are you sure?',
            text: "You won't be able to revert this!",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Yes, delete it!',
            cancelButtonText: 'No, cancel!',
            reverseButtons: true
        });

        if (result.isConfirmed) {
            try {
                await dispatch(deleteDeliveryNote(id));
                toast.success('DeliveryNote deleted successfully', {
                    position: "top-right",
                    autoClose: 3000,
                    hideProgressBar: false,
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: true,
                });
                return true;
            } catch (error) {
                toast.error(error.message || 'Error deleting deliverynote', {
                    position: "top-right",
                    autoClose: 3000,
                    hideProgressBar: false,
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: true,
                });
                return false;
            }
        }
        return false;
    };

    const columns = [
        {
            name: 'Voucher Number',
            selector: row => row.voucher_number,
            minWidth: '170px'
        },
        {
            name: 'Voucher Date',
            selector: row => row.voucher_date,
            sortable: true,
            sortField: 'delivery_notes.delivery_date',
        },
        {
            name: 'LPO Number',
            selector: row => row.lpo_number,
        },
        {
            name: 'Customer Name',
            selector: row => row.customer_name,
        },
        {
            name: 'Net Total',
            selector: row => row.net_total,
        },
    ];


    const actions = {
        fetch: (queryFilter, activeFilter, shouldUseFilter, externalCondition) => dispatch(fetchDeliverynotes(queryFilter, activeFilter, shouldUseFilter, externalCondition)),
        create: handleCreate,
        update: handleUpdate,
        delete: handleDelete,
        search: (search) => dispatch(searchDeliverynotes(search)),
        setPagination: (pagination) => dispatch(setPagination(pagination)),
        setSort: (sort) => dispatch(setSort(sort)),
        setDefaultFilter: (filter) => dispatch(setDefaultFilter(filter)),
        changeCondition: (index, condition) => dispatch(changeCondition(index, condition)),
        addNewCondition: (rule) => dispatch(addNewCondition(rule)),
        updateOptionRule: (index, rule) => dispatch(updateOptionRule(index, rule)),
        updateOptionValue: (index, value) => dispatch(updateOptionValue(index, value)),
        removeCondition: (index) => dispatch(removeCondition(index)),
        saveFilterCondition: (filter) => dispatch(saveFilterCondition(filter)),
        setSelected: (selectedRows) => dispatch(setSelected(selectedRows)),
    };

    let defaultAvailableActions = ['search', 'filter'];
    if (hasCreatePermission) {
        defaultAvailableActions = ['create', ...defaultAvailableActions];
    }
    if (hasViewPermission || hasUpdatePermission) {
        defaultAvailableActions = ['actions', 'edit', ...defaultAvailableActions];
    }
    const availableActions = defaultAvailableActions.filter(action => !excludedActions.includes(action));

    return (
        <>
            <GenericPage
                title="Delivery Notes"
                moduleName="DeliveryNote"
                moduleKey="deliverynotes"
                state={deliverynoteState}
                actions={actions}
                FormComponent={DeliveryNoteForm}
                columns={columns}
                availableActions={availableActions}
                actionsColumnWidth="150px"
                urlKey="delivery_notes"
                actionType="url"
                onSelectedRowsChange={onSelectedRowsChange}
                onClickedRow={onClickedRow}
                
                selectedIds={selectedIds}
                externalCondition={externalCondition}
            />
        </>
    );
};

export default Deliverynotes; 